// SolarInstallationIndustries.js - Solar installation themed Industries section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a solar installation themed Industries section with tabs and visual elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the industries section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The industries section elements
 */
export const createSolarInstallationIndustriesElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const industriesSectionClass = 'solar-industries-section';
  const industriesContainerClass = 'solar-industries-container';
  const industriesHeaderClass = 'solar-industries-header';
  const industriesTitleClass = 'solar-industries-title';
  const industriesSubtitleClass = 'solar-industries-subtitle';
  const industriesContentClass = 'solar-industries-content';
  const industriesTabsClass = 'solar-industries-tabs';
  const industriesTabClass = 'solar-industries-tab';
  const industriesTabActiveClass = 'solar-industries-tab-active';
  const industriesTabsContentClass = 'solar-industries-tabs-content';
  const industriesTabContentClass = 'solar-industries-tab-content';
  const industriesTabContentActiveClass = 'solar-industries-tab-content-active';
  const industriesImageClass = 'solar-industries-image';
  const industriesInfoClass = 'solar-industries-info';
  const industriesInfoTitleClass = 'solar-industries-info-title';
  const industriesInfoDescriptionClass = 'solar-industries-info-description';
  const industriesChecklistClass = 'solar-industries-checklist';
  const industriesChecklistItemClass = 'solar-industries-checklist-item';
  const industriesCheckIconClass = 'solar-industries-check-icon';
  const industriesStatsClass = 'solar-industries-stats';
  const industriesStatClass = 'solar-industries-stat';
  const industriesStatNumberClass = 'solar-industries-stat-number';
  const industriesStatLabelClass = 'solar-industries-stat-label';
  const industriesCTAClass = 'solar-industries-cta';
  const industriesCTAButtonClass = 'solar-industries-cta-button';
  
  // Define styles - Solar themed with deep blue and green colors
  const industriesSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 20% 30%, rgba(74, 222, 128, 0.12) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(34, 197, 94, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(15, 35, 65, 0.05) 0%, transparent 70%),
      linear-gradient(135deg, #f8fafc 0%, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%, #f8fafc 100%)
    `,
    color: '#1e293b',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const industriesContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  const industriesHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '50px',
    maxWidth: '800px'
  };

  const industriesTitleStyles = {
    fontSize: '48px',
    fontWeight: '700',
    marginBottom: '20px',
    color: '#1e293b',
    lineHeight: '1.2',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesSubtitleStyles = {
    fontSize: '20px',
    lineHeight: '1.6',
    color: '#475569',
    maxWidth: '700px',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: '30px'
  };

  const industriesTabsStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '40px',
    flexWrap: 'wrap',
    width: '100%'
  };

  const industriesTabStyles = {
    padding: '14px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#64748b',
    backgroundColor: '#ffffff',
    border: '1px solid rgba(74, 222, 128, 0.3)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesTabActiveStyles = {
    color: '#ffffff',
    backgroundColor: '#22c55e',
    borderColor: '#22c55e',
    boxShadow: '0 4px 10px rgba(34, 197, 94, 0.3)'
  };

  const industriesTabsContentStyles = {
    width: '100%',
    position: 'relative'
  };

  const industriesTabContentStyles = {
    display: 'none',
    opacity: '0',
    transition: 'opacity 0.3s ease',
    width: '100%'
  };

  const industriesTabContentActiveStyles = {
    display: 'flex',
    opacity: '1',
    flexDirection: 'row',
    gap: '60px',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const industriesImageStyles = {
    width: '48%',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    objectFit: 'cover',
    objectPosition: 'center',
    border: '2px solid rgba(34, 197, 94, 0.2)'
  };

  const industriesInfoStyles = {
    width: '48%',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const industriesInfoTitleStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '16px',
    lineHeight: '1.3',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesInfoDescriptionStyles = {
    fontSize: '18px',
    lineHeight: '1.7',
    color: '#475569',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesChecklistStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
    marginBottom: '32px'
  };

  const industriesChecklistItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    fontSize: '16px',
    lineHeight: '1.5',
    color: '#475569',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesCheckIconStyles = {
    width: '20px',
    height: '20px',
    minWidth: '20px',
    color: '#22c55e',
    marginTop: '3px'
  };

  const industriesStatsStyles = {
    display: 'flex',
    gap: '40px',
    marginTop: '20px',
    marginBottom: '32px'
  };

  const industriesStatStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  };

  const industriesStatNumberStyles = {
    fontSize: '36px',
    fontWeight: '700',
    color: '#22c55e',
    marginBottom: '4px',
    lineHeight: '1',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesStatLabelStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesCTAStyles = {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  };

  const industriesCTAButtonStyles = {
    padding: '14px 28px',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#22c55e',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 10px rgba(34, 197, 94, 0.3)',
    fontFamily: '"Inter", sans-serif'
  };

  const industriesCTAButtonHoverStyles = {
    backgroundColor: '#16a34a',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 15px rgba(34, 197, 94, 0.4)'
  };

  // Tablet Styles
  const tabletIndustriesTabContentActiveStyles = {
    flexDirection: 'column',
    gap: '40px'
  };

  const tabletIndustriesImageStyles = {
    width: '100%',
    maxWidth: '600px',
    marginBottom: '20px'
  };

  const tabletIndustriesInfoStyles = {
    width: '100%'
  };

  const tabletIndustriesStatsStyles = {
    justifyContent: 'space-between',
    gap: '20px',
    flexWrap: 'wrap'
  };

  // Mobile Styles
  const mobileIndustriesTabsStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  };

  const mobileIndustriesTitleStyles = {
    fontSize: '36px'
  };

  const mobileIndustriesCTAStyles = {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '15px',
    width: '100%'
  };

  const mobileIndustriesCTAButtonStyles = {
    width: '100%',
    textAlign: 'center'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(industriesSectionClass, industriesSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesContainerClass, industriesContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesHeaderClass, industriesHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesTitleClass, industriesTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesSubtitleClass, industriesSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesContentClass, industriesContentStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesTabsClass, industriesTabsStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesTabClass, industriesTabStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesTabActiveClass, industriesTabActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesTabsContentClass, industriesTabsContentStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesTabContentClass, industriesTabContentStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesTabContentActiveClass, industriesTabContentActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesImageClass, industriesImageStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesInfoClass, industriesInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesInfoTitleClass, industriesInfoTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesInfoDescriptionClass, industriesInfoDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesChecklistClass, industriesChecklistStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesChecklistItemClass, industriesChecklistItemStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesCheckIconClass, industriesCheckIconStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesStatsClass, industriesStatsStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesStatClass, industriesStatStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesStatNumberClass, industriesStatNumberStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesStatLabelClass, industriesStatLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesCTAClass, industriesCTAStyles);
    newRegistry[DEVICES.DESKTOP].set(industriesCTAButtonClass, industriesCTAButtonStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${industriesCTAButtonClass}--hover`, industriesCTAButtonHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(industriesTabContentActiveClass, tabletIndustriesTabContentActiveStyles);
    newRegistry[DEVICES.TABLET].set(industriesImageClass, tabletIndustriesImageStyles);
    newRegistry[DEVICES.TABLET].set(industriesInfoClass, tabletIndustriesInfoStyles);
    newRegistry[DEVICES.TABLET].set(industriesStatsClass, tabletIndustriesStatsStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(industriesTabsClass, mobileIndustriesTabsStyles);
    newRegistry[DEVICES.MOBILE].set(industriesTitleClass, mobileIndustriesTitleStyles);
    newRegistry[DEVICES.MOBILE].set(industriesCTAClass, mobileIndustriesCTAStyles);
    newRegistry[DEVICES.MOBILE].set(industriesCTAButtonClass, mobileIndustriesCTAButtonStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    industriesSectionClass,
    industriesContainerClass,
    industriesHeaderClass,
    industriesTitleClass,
    industriesSubtitleClass,
    industriesContentClass,
    industriesTabsClass,
    industriesTabClass,
    industriesTabActiveClass,
    industriesTabsContentClass,
    industriesTabContentClass,
    industriesTabContentActiveClass,
    industriesImageClass,
    industriesInfoClass,
    industriesInfoTitleClass,
    industriesInfoDescriptionClass,
    industriesChecklistClass,
    industriesChecklistItemClass,
    industriesCheckIconClass,
    industriesStatsClass,
    industriesStatClass,
    industriesStatNumberClass,
    industriesStatLabelClass,
    industriesCTAClass,
    industriesCTAButtonClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  const contentId = Date.now() + 5;
  
  // Tabs
  const tabsId = Date.now() + 6;
  const tab1Id = Date.now() + 7;
  const tab2Id = Date.now() + 8;
  const tab3Id = Date.now() + 9;
  
  // Tabs content
  const tabsContentId = Date.now() + 10;

  // Tab content IDs
  const tab1ContentId = Date.now() + 11;
  const tab1ImageId = Date.now() + 12;
  const tab1InfoId = Date.now() + 13;
  const tab1InfoTitleId = Date.now() + 14;
  const tab1InfoDescriptionId = Date.now() + 15;
  const tab1ChecklistId = Date.now() + 16;
  const tab1ChecklistItem1Id = Date.now() + 17;
  const tab1ChecklistItem2Id = Date.now() + 18;
  const tab1ChecklistItem3Id = Date.now() + 19;
  const tab1CheckIcon1Id = Date.now() + 20;
  const tab1CheckIcon2Id = Date.now() + 21;
  const tab1CheckIcon3Id = Date.now() + 22;
  const tab1StatsId = Date.now() + 23;
  const tab1Stat1Id = Date.now() + 24;
  const tab1Stat1NumberId = Date.now() + 25;
  const tab1Stat1LabelId = Date.now() + 26;
  const tab1Stat2Id = Date.now() + 27;
  const tab1Stat2NumberId = Date.now() + 28;
  const tab1Stat2LabelId = Date.now() + 29;
  const tab1CTAId = Date.now() + 30;
  const tab1CTAButtonId = Date.now() + 31;
  
  // Tab 2 content IDs
  const tab2ContentId = Date.now() + 32;
  const tab2ImageId = Date.now() + 33;
  const tab2InfoId = Date.now() + 34;
  const tab2InfoTitleId = Date.now() + 35;
  const tab2InfoDescriptionId = Date.now() + 36;
  const tab2ChecklistId = Date.now() + 37;
  const tab2ChecklistItem1Id = Date.now() + 38;
  const tab2ChecklistItem2Id = Date.now() + 39;
  const tab2ChecklistItem3Id = Date.now() + 40;
  const tab2CheckIcon1Id = Date.now() + 41;
  const tab2CheckIcon2Id = Date.now() + 42;
  const tab2CheckIcon3Id = Date.now() + 43;
  const tab2StatsId = Date.now() + 44;
  const tab2Stat1Id = Date.now() + 45;
  const tab2Stat1NumberId = Date.now() + 46;
  const tab2Stat1LabelId = Date.now() + 47;
  const tab2Stat2Id = Date.now() + 48;
  const tab2Stat2NumberId = Date.now() + 49;
  const tab2Stat2LabelId = Date.now() + 50;
  const tab2CTAId = Date.now() + 51;
  const tab2CTAButtonId = Date.now() + 52;
  
  // Tab 3 content IDs
  const tab3ContentId = Date.now() + 53;
  const tab3ImageId = Date.now() + 54;
  const tab3InfoId = Date.now() + 55;
  const tab3InfoTitleId = Date.now() + 56;
  const tab3InfoDescriptionId = Date.now() + 57;
  const tab3ChecklistId = Date.now() + 58;
  const tab3ChecklistItem1Id = Date.now() + 59;
  const tab3ChecklistItem2Id = Date.now() + 60;
  const tab3ChecklistItem3Id = Date.now() + 61;
  const tab3CheckIcon1Id = Date.now() + 62;
  const tab3CheckIcon2Id = Date.now() + 63;
  const tab3CheckIcon3Id = Date.now() + 64;
  const tab3StatsId = Date.now() + 65;
  const tab3Stat1Id = Date.now() + 66;
  const tab3Stat1NumberId = Date.now() + 67;
  const tab3Stat1LabelId = Date.now() + 68;
  const tab3Stat2Id = Date.now() + 69;
  const tab3Stat2NumberId = Date.now() + 70;
  const tab3Stat2LabelId = Date.now() + 71;
  const tab3CTAId = Date.now() + 72;
  const tab3CTAButtonId = Date.now() + 73;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Industries Section',
      parentId: null,
      className: industriesSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'industries-section',
        'data-component': 'section',
        'data-section-type': 'industries',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Industries Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: industriesContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'industries-container'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Industries Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: industriesHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'industries-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Industries Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: industriesTitleClass,
      styles: {},
      children: [],
      text: 'Industries We Serve',
      attributes: {
        'data-element': 'industries-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Industries Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: industriesSubtitleClass,
      styles: {},
      children: [],
      text: 'From residential homes to large commercial complexes, we provide comprehensive solar and electrical solutions across diverse industries, ensuring reliable and sustainable energy systems.',
      attributes: {
        'data-element': 'industries-subtitle'
      }
    },
    
    // Content container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Industries Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: industriesContentClass,
      styles: {},
      children: [tabsId, tabsContentId],
      text: '',
      attributes: {
        'data-element': 'industries-content'
      }
    },
    
    // Tabs
    {
      id: tabsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Industries Tabs',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: industriesTabsClass,
      styles: {},
      children: [tab1Id, tab2Id, tab3Id],
      text: '',
      attributes: {
        'data-element': 'industries-tabs'
      }
    },
    
    // Tab 1 - Residential
    {
      id: tab1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Tab 1',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: `${industriesTabClass} ${industriesTabActiveClass}`,
      styles: {},
      children: [],
      text: 'Residential',
      attributes: {
        'data-element': 'industries-tab',
        'data-tab': 'tab-1',
        'data-active': 'true'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1000,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: industriesTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: industriesTabContentActiveClass
            },
            label: `Tab Click: ${industriesTabActiveClass} & ${industriesTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tab 2 - Commercial
    {
      id: tab2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Tab 2',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: industriesTabClass,
      styles: {},
      children: [],
      text: 'Commercial',
      attributes: {
        'data-element': 'industries-tab',
        'data-tab': 'tab-2',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1001,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: industriesTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: industriesTabContentActiveClass
            },
            label: `Tab Click: ${industriesTabActiveClass} & ${industriesTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tab 3 - Industrial
    {
      id: tab3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Tab 3',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: industriesTabClass,
      styles: {},
      children: [],
      text: 'Industrial',
      attributes: {
        'data-element': 'industries-tab',
        'data-tab': 'tab-3',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1002,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: industriesTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: industriesTabContentActiveClass
            },
            label: `Tab Click: ${industriesTabActiveClass} & ${industriesTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tabs content container
    {
      id: tabsContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tabs Content',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: industriesTabsContentClass,
      styles: {},
      children: [tab1ContentId, tab2ContentId, tab3ContentId],
      text: '',
      attributes: {
        'data-element': 'industries-tabs-content'
      }
    },
    
    // Tab 1 content - Residential
    {
      id: tab1ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: `${industriesTabContentClass} ${industriesTabContentActiveClass}`,
      styles: {},
      children: [tab1ImageId, tab1InfoId],
      text: '',
      attributes: {
        'data-element': 'industries-tab-content',
        'data-tab-content': 'tab-1',
        'data-active': 'true'
      }
    },
    
    // Tab 1 Image
    {
      id: tab1ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 1 Image',
      parentId: tab1ContentId,
      hierarchyName: getHierarchyName('image'),
      className: industriesImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'industries-image',
        'alt': 'Residential Solar Installation'
      }
    },
    
    // Tab 1 Info
    {
      id: tab1InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Info',
      parentId: tab1ContentId,
      hierarchyName: getHierarchyName('div'),
      className: industriesInfoClass,
      styles: {},
      children: [tab1InfoTitleId, tab1InfoDescriptionId, tab1ChecklistId, tab1StatsId, tab1CTAId],
      text: '',
      attributes: {
        'data-element': 'industries-info'
      }
    },
    
    // Tab 1 Info Title
    {
      id: tab1InfoTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Tab 1 Info Title',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: industriesInfoTitleClass,
      styles: {},
      children: [],
      text: 'Residential Solar Solutions',
      attributes: {
        'data-element': 'industries-info-title'
      }
    },
    
    // Tab 1 Info Description
    {
      id: tab1InfoDescriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Tab 1 Info Description',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('p'),
      className: industriesInfoDescriptionClass,
      styles: {},
      children: [],
      text: 'Transform your home with our comprehensive residential solar solutions. From rooftop installations to smart home integration, we provide everything you need to reduce energy costs and increase property value.',
      attributes: {
        'data-element': 'industries-info-description'
      }
    },
    
    // Tab 1 Checklist
    {
      id: tab1ChecklistId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Checklist',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistClass,
      styles: {},
      children: [tab1ChecklistItem1Id, tab1ChecklistItem2Id, tab1ChecklistItem3Id],
      text: '',
      attributes: {
        'data-element': 'industries-checklist'
      }
    },
    
    // Tab 1 Checklist Items
    {
      id: tab1ChecklistItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Checklist Item 1',
      parentId: tab1ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab1CheckIcon1Id],
      text: 'Custom solar panel design and installation',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab1CheckIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 1 Check Icon 1',
      parentId: tab1ChecklistItem1Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    {
      id: tab1ChecklistItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Checklist Item 2',
      parentId: tab1ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab1CheckIcon2Id],
      text: 'Smart home security and automation systems',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab1CheckIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 1 Check Icon 2',
      parentId: tab1ChecklistItem2Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    {
      id: tab1ChecklistItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Checklist Item 3',
      parentId: tab1ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab1CheckIcon3Id],
      text: 'Energy monitoring and maintenance services',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab1CheckIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 1 Check Icon 3',
      parentId: tab1ChecklistItem3Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 1 Stats
    {
      id: tab1StatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stats',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatsClass,
      styles: {},
      children: [tab1Stat1Id, tab1Stat2Id],
      text: '',
      attributes: {
        'data-element': 'industries-stats'
      }
    },
    
    // Tab 1 Stat 1
    {
      id: tab1Stat1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 1',
      parentId: tab1StatsId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatClass,
      styles: {},
      children: [tab1Stat1NumberId, tab1Stat1LabelId],
      text: '',
      attributes: {
        'data-element': 'industries-stat'
      }
    },
    
    {
      id: tab1Stat1NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 1 Number',
      parentId: tab1Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatNumberClass,
      styles: {},
      children: [],
      text: '800+',
      attributes: {
        'data-element': 'industries-stat-number'
      }
    },
    
    {
      id: tab1Stat1LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 1 Label',
      parentId: tab1Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatLabelClass,
      styles: {},
      children: [],
      text: 'HOMES POWERED',
      attributes: {
        'data-element': 'industries-stat-label'
      }
    },
    
    // Tab 1 Stat 2
    {
      id: tab1Stat2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 2',
      parentId: tab1StatsId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatClass,
      styles: {},
      children: [tab1Stat2NumberId, tab1Stat2LabelId],
      text: '',
      attributes: {
        'data-element': 'industries-stat'
      }
    },
    
    {
      id: tab1Stat2NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 2 Number',
      parentId: tab1Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatNumberClass,
      styles: {},
      children: [],
      text: '35%',
      attributes: {
        'data-element': 'industries-stat-number'
      }
    },
    
    {
      id: tab1Stat2LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 2 Label',
      parentId: tab1Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatLabelClass,
      styles: {},
      children: [],
      text: 'ENERGY SAVINGS',
      attributes: {
        'data-element': 'industries-stat-label'
      }
    },
    
    // Tab 1 CTA
    {
      id: tab1CTAId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 CTA',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesCTAClass,
      styles: {},
      children: [tab1CTAButtonId],
      text: '',
      attributes: {
        'data-element': 'industries-cta'
      }
    },
    
    {
      id: tab1CTAButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Tab 1 CTA Button',
      parentId: tab1CTAId,
      hierarchyName: getHierarchyName('button'),
      className: industriesCTAButtonClass,
      styles: {},
      children: [],
      text: 'Get Residential Quote',
      linkTo: 'get-quote',
      attributes: {
        'data-element': 'industries-cta-button'
      }
    },
    
    // Tab 2 content - Commercial (simplified for brevity)
    {
      id: tab2ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: industriesTabContentClass,
      styles: {},
      children: [tab2ImageId, tab2InfoId],
      text: '',
      attributes: {
        'data-element': 'industries-tab-content',
        'data-tab-content': 'tab-2',
        'data-active': 'false'
      }
    },
    
    {
      id: tab2ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 2 Image',
      parentId: tab2ContentId,
      hierarchyName: getHierarchyName('image'),
      className: industriesImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'industries-image',
        'alt': 'Commercial Solar Installation'
      }
    },
    
    {
      id: tab2InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Info',
      parentId: tab2ContentId,
      hierarchyName: getHierarchyName('div'),
      className: industriesInfoClass,
      styles: {},
      children: [tab2InfoTitleId, tab2InfoDescriptionId, tab2ChecklistId, tab2StatsId, tab2CTAId],
      text: '',
      attributes: {
        'data-element': 'industries-info'
      }
    },
    
    {
      id: tab2InfoTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Tab 2 Info Title',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: industriesInfoTitleClass,
      styles: {},
      children: [],
      text: 'Commercial Solar Systems',
      attributes: {
        'data-element': 'industries-info-title'
      }
    },
    
    {
      id: tab2InfoDescriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Tab 2 Info Description',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('p'),
      className: industriesInfoDescriptionClass,
      styles: {},
      children: [],
      text: 'Maximize your business efficiency with our commercial solar solutions. From office buildings to retail spaces, we design scalable systems that reduce operational costs and enhance your corporate sustainability profile.',
      attributes: {
        'data-element': 'industries-info-description'
      }
    },
    
    {
      id: tab2ChecklistId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Checklist',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistClass,
      styles: {},
      children: [tab2ChecklistItem1Id, tab2ChecklistItem2Id, tab2ChecklistItem3Id],
      text: '',
      attributes: {
        'data-element': 'industries-checklist'
      }
    },
    
    {
      id: tab2ChecklistItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Checklist Item 1',
      parentId: tab2ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab2CheckIcon1Id],
      text: 'Large-scale solar installations for businesses',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab2CheckIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 2 Check Icon 1',
      parentId: tab2ChecklistItem1Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    {
      id: tab2ChecklistItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Checklist Item 2',
      parentId: tab2ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab2CheckIcon2Id],
      text: 'Advanced security and access control systems',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab2CheckIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 2 Check Icon 2',
      parentId: tab2ChecklistItem2Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    {
      id: tab2ChecklistItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Checklist Item 3',
      parentId: tab2ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab2CheckIcon3Id],
      text: 'Energy management and monitoring solutions',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab2CheckIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 2 Check Icon 3',
      parentId: tab2ChecklistItem3Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    {
      id: tab2StatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stats',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatsClass,
      styles: {},
      children: [tab2Stat1Id, tab2Stat2Id],
      text: '',
      attributes: {
        'data-element': 'industries-stats'
      }
    },
    
    {
      id: tab2Stat1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 1',
      parentId: tab2StatsId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatClass,
      styles: {},
      children: [tab2Stat1NumberId, tab2Stat1LabelId],
      text: '',
      attributes: {
        'data-element': 'industries-stat'
      }
    },
    
    {
      id: tab2Stat1NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 1 Number',
      parentId: tab2Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatNumberClass,
      styles: {},
      children: [],
      text: '150+',
      attributes: {
        'data-element': 'industries-stat-number'
      }
    },
    
    {
      id: tab2Stat1LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 1 Label',
      parentId: tab2Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatLabelClass,
      styles: {},
      children: [],
      text: 'BUSINESSES SERVED',
      attributes: {
        'data-element': 'industries-stat-label'
      }
    },
    
    {
      id: tab2Stat2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 2',
      parentId: tab2StatsId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatClass,
      styles: {},
      children: [tab2Stat2NumberId, tab2Stat2LabelId],
      text: '',
      attributes: {
        'data-element': 'industries-stat'
      }
    },
    
    {
      id: tab2Stat2NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 2 Number',
      parentId: tab2Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatNumberClass,
      styles: {},
      children: [],
      text: '45%',
      attributes: {
        'data-element': 'industries-stat-number'
      }
    },
    
    {
      id: tab2Stat2LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 2 Label',
      parentId: tab2Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatLabelClass,
      styles: {},
      children: [],
      text: 'COST REDUCTION',
      attributes: {
        'data-element': 'industries-stat-label'
      }
    },
    
    {
      id: tab2CTAId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 CTA',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesCTAClass,
      styles: {},
      children: [tab2CTAButtonId],
      text: '',
      attributes: {
        'data-element': 'industries-cta'
      }
    },
    
    {
      id: tab2CTAButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Tab 2 CTA Button',
      parentId: tab2CTAId,
      hierarchyName: getHierarchyName('button'),
      className: industriesCTAButtonClass,
      styles: {},
      children: [],
      text: 'Get Commercial Quote',
      linkTo: 'get-quote',
      attributes: {
        'data-element': 'industries-cta-button'
      }
    },
    
    // Tab 3 content - Industrial (simplified)
    {
      id: tab3ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: industriesTabContentClass,
      styles: {},
      children: [tab3ImageId, tab3InfoId],
      text: '',
      attributes: {
        'data-element': 'industries-tab-content',
        'data-tab-content': 'tab-3',
        'data-active': 'false'
      }
    },
    
    {
      id: tab3ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 3 Image',
      parentId: tab3ContentId,
      hierarchyName: getHierarchyName('image'),
      className: industriesImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'industries-image',
        'alt': 'Industrial Solar Installation'
      }
    },
    
    {
      id: tab3InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Info',
      parentId: tab3ContentId,
      hierarchyName: getHierarchyName('div'),
      className: industriesInfoClass,
      styles: {},
      children: [tab3InfoTitleId, tab3InfoDescriptionId, tab3ChecklistId, tab3StatsId, tab3CTAId],
      text: '',
      attributes: {
        'data-element': 'industries-info'
      }
    },
    
    {
      id: tab3InfoTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Tab 3 Info Title',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: industriesInfoTitleClass,
      styles: {},
      children: [],
      text: 'Industrial Energy Solutions',
      attributes: {
        'data-element': 'industries-info-title'
      }
    },
    
    {
      id: tab3InfoDescriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Tab 3 Info Description',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('p'),
      className: industriesInfoDescriptionClass,
      styles: {},
      children: [],
      text: 'Power your industrial operations with our robust energy solutions. From manufacturing facilities to warehouses, we provide high-capacity systems designed for demanding industrial environments.',
      attributes: {
        'data-element': 'industries-info-description'
      }
    },
    
    {
      id: tab3ChecklistId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Checklist',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistClass,
      styles: {},
      children: [tab3ChecklistItem1Id, tab3ChecklistItem2Id, tab3ChecklistItem3Id],
      text: '',
      attributes: {
        'data-element': 'industries-checklist'
      }
    },
    
    {
      id: tab3ChecklistItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Checklist Item 1',
      parentId: tab3ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab3CheckIcon1Id],
      text: 'High-capacity industrial solar installations',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab3CheckIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 3 Check Icon 1',
      parentId: tab3ChecklistItem1Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    {
      id: tab3ChecklistItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Checklist Item 2',
      parentId: tab3ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab3CheckIcon2Id],
      text: 'Industrial-grade security and communication systems',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab3CheckIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 3 Check Icon 2',
      parentId: tab3ChecklistItem2Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    {
      id: tab3ChecklistItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Checklist Item 3',
      parentId: tab3ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: industriesChecklistItemClass,
      styles: {},
      children: [tab3CheckIcon3Id],
      text: '24/7 monitoring and maintenance support',
      attributes: {
        'data-element': 'industries-checklist-item'
      }
    },
    
    {
      id: tab3CheckIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 3 Check Icon 3',
      parentId: tab3ChecklistItem3Id,
      hierarchyName: getHierarchyName('image'),
      className: industriesCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%234ade80&width=20&height=20',
      attributes: {
        'data-element': 'industries-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    {
      id: tab3StatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stats',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatsClass,
      styles: {},
      children: [tab3Stat1Id, tab3Stat2Id],
      text: '',
      attributes: {
        'data-element': 'industries-stats'
      }
    },
    
    {
      id: tab3Stat1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 1',
      parentId: tab3StatsId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatClass,
      styles: {},
      children: [tab3Stat1NumberId, tab3Stat1LabelId],
      text: '',
      attributes: {
        'data-element': 'industries-stat'
      }
    },
    
    {
      id: tab3Stat1NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 1 Number',
      parentId: tab3Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatNumberClass,
      styles: {},
      children: [],
      text: '25MW',
      attributes: {
        'data-element': 'industries-stat-number'
      }
    },
    
    {
      id: tab3Stat1LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 1 Label',
      parentId: tab3Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatLabelClass,
      styles: {},
      children: [],
      text: 'CAPACITY INSTALLED',
      attributes: {
        'data-element': 'industries-stat-label'
      }
    },
    
    {
      id: tab3Stat2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 2',
      parentId: tab3StatsId,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatClass,
      styles: {},
      children: [tab3Stat2NumberId, tab3Stat2LabelId],
      text: '',
      attributes: {
        'data-element': 'industries-stat'
      }
    },
    
    {
      id: tab3Stat2NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 2 Number',
      parentId: tab3Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatNumberClass,
      styles: {},
      children: [],
      text: '60%',
      attributes: {
        'data-element': 'industries-stat-number'
      }
    },
    
    {
      id: tab3Stat2LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 2 Label',
      parentId: tab3Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: industriesStatLabelClass,
      styles: {},
      children: [],
      text: 'EFFICIENCY GAIN',
      attributes: {
        'data-element': 'industries-stat-label'
      }
    },
    
    {
      id: tab3CTAId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 CTA',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('div'),
      className: industriesCTAClass,
      styles: {},
      children: [tab3CTAButtonId],
      text: '',
      attributes: {
        'data-element': 'industries-cta'
      }
    },
    
    {
      id: tab3CTAButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Tab 3 CTA Button',
      parentId: tab3CTAId,
      hierarchyName: getHierarchyName('button'),
      className: industriesCTAButtonClass,
      styles: {},
      children: [],
      text: 'Get Industrial Quote',
      linkTo: 'get-quote',
      attributes: {
        'data-element': 'industries-cta-button'
      }
    }
  ];

  return {
    industriesSection: newElements[0],
    elements: newElements
  };
} 